<script>
  import { onMount } from 'svelte'
  import { getSessions, revokeSession, replace } from '../../api.js'
  import { SITE_MODE } from '../../lib/hostSite.js'

  const sessionsReturnPath = SITE_MODE === 'hub' ? '/hub/settings' : '/dashboard/sessions'

  let sessions = []
  let loading = true
  let err = ''
  let busyJti = ''

  function fmtWhen(ts) {
    if (!ts) return '—'
    try {
      const d = new Date(Number(ts) * 1000)
      const utc = d.toLocaleString('ru-RU', {
        timeZone: 'UTC',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
      const msk = d.toLocaleString('ru-RU', {
        timeZone: 'Europe/Moscow',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
      return `${utc} UTC · ${msk} MSK`
    } catch {
      return '—'
    }
  }

  async function load() {
    loading = true
    err = ''
    try {
      const r = await getSessions()
      if (r.unauthorized) return
      if (!r.ok) {
        err = (r.data && r.data.error) || 'не удалось загрузить'
        sessions = []
      } else {
        sessions = (r.data && r.data.sessions) || []
      }
    } catch {
      err = 'сеть недоступна'
      sessions = []
    } finally {
      loading = false
    }
  }

  async function onRevoke(row) {
    if (!row || !row.jti || busyJti) return
    const self = !!row.current
    const label = self ? 'текущую сессию (выйдете)' : 'эту сессию'
    if (!window.confirm(`Отозвать ${label}?`)) return
    busyJti = row.jti
    err = ''
    try {
      const r = await revokeSession(row.jti)
      if (r.unauthorized) return
      if (!r.ok) {
        err = (r.data && r.data.error) || 'отзыв не удался'
        return
      }
      if (r.data && r.data.self) {
        replace('/login?next=' + encodeURIComponent(sessionsReturnPath))
        return
      }
      await load()
    } catch {
      err = 'сеть недоступна'
    } finally {
      busyJti = ''
    }
  }

  onMount(load)
</script>

<section class="pm-surface sessions" aria-label="Активные сессии">
  <div class="pm-section-head">
    <h2>Активные сессии</h2>
    <button type="button" class="pm-btn pm-btn--ghost" on:click={load} disabled={loading}>
      Обновить
    </button>
  </div>
  <p class="pm-meta sessions__hint">
    Устройства с действующим входом. Отзыв сразу инвалидирует cookie на том устройстве.
  </p>

  {#if err}
    <p class="sessions__err" role="alert">{err}</p>
  {/if}

  {#if loading}
    <p class="pm-meta">Загрузка…</p>
  {:else if !sessions.length}
    <p class="pm-meta">Нет активных сессий.</p>
  {:else}
    <ul class="sessions__list">
      {#each sessions as row (row.jti)}
        <li class="sessions__row" class:is-current={row.current}>
          <div class="sessions__main">
            <p class="sessions__device">
              {row.ua_label || 'unknown'}
              {#if row.current}
                <span class="sessions__badge">эта</span>
              {/if}
            </p>
            <p class="sessions__meta">
              IP {row.ip || '—'} · {row.geo || 'geo n/a'}
            </p>
            <p class="sessions__meta">
              вход {fmtWhen(row.created_at)}
            </p>
            <p class="sessions__meta">
              активность {fmtWhen(row.last_seen)}
            </p>
          </div>
          <button
            type="button"
            class="pm-btn pm-btn--ghost sessions__revoke"
            disabled={!!busyJti}
            on:click={() => onRevoke(row)}
          >
            {busyJti === row.jti ? '…' : row.current ? 'Выйти здесь' : 'Отозвать'}
          </button>
        </li>
      {/each}
    </ul>
  {/if}
</section>

<style>
  .sessions__hint {
    margin: 0 0 1rem;
  }
  .sessions__err {
    margin: 0 0 0.75rem;
    color: var(--pm-bad);
    font-size: 0.85rem;
  }
  .sessions__list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 0.65rem;
  }
  .sessions__row {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.75rem 1rem;
    padding: 0.85rem 0.95rem;
    border: 1px solid var(--pm-glass-border);
    border-radius: 10px;
    background: var(--pm-glass);
  }
  .sessions__row.is-current {
    border-color: color-mix(in srgb, var(--pm-ok) 45%, var(--pm-glass-border));
  }
  .sessions__device {
    margin: 0 0 0.25rem;
    font-weight: 600;
    color: var(--pm-ink);
  }
  .sessions__badge {
    display: inline-block;
    margin-left: 0.35rem;
    padding: 0.05rem 0.4rem;
    border-radius: 999px;
    font-size: 0.65rem;
    font-weight: 500;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--pm-ok);
    border: 1px solid color-mix(in srgb, var(--pm-ok) 40%, transparent);
  }
  .sessions__meta {
    margin: 0.1rem 0 0;
    font-family: var(--pm-mono);
    font-size: 0.72rem;
    color: var(--pm-ink-soft);
  }
  .sessions__revoke {
    flex-shrink: 0;
  }
</style>
